import {
  AlertCircle,
  CheckCircle2,
  Download,
  FileOutput,
  Loader2,
  X,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import type { ViewerSource } from "@/types/takeoff-pilot";
import { Button } from "@/components/ui/button";
import {
  GLOBAL_PROCESSING_COMPLETED,
  GLOBAL_PROCESSING_FAILED,
  GLOBAL_PROCESSING_PENDING,
  GLOBAL_PROCESSING_PROCESSING,
  type ProcessingStateWithIdle,
} from "@/lib/processing-states";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { ReactNode } from "react";

interface TakeoffViewerHeaderProps {
  viewerSources: ViewerSource[];
  selectedPlanDocumentId: string;
  onSelectPlanDocumentId: (value: string) => void;
  onClose: () => void;
  onDownloadWorkbook: () => void;
  onExportPdf: () => void;
  onDownloadPdf: () => void;
  exportStatus: ProcessingStateWithIdle;
  exportError?: string;
  isPdfExportInProgress: boolean;
  canExportPdf: boolean;
  canDownloadPdf: boolean;
  canDownload: boolean;
  tagsCount?: number;
  activePage?: number;
  pageCount?: number;
  onGoToPage?: (page: number) => void;
  toolsets?: ReactNode;
}

export function TakeoffViewerHeader({
  viewerSources,
  selectedPlanDocumentId,
  onSelectPlanDocumentId,
  onClose,
  onDownloadWorkbook,
  onExportPdf,
  onDownloadPdf,
  exportStatus,
  exportError,
  isPdfExportInProgress,
  canExportPdf,
  canDownloadPdf,
  canDownload,
  tagsCount = 0,
  activePage = 1,
  pageCount = 1,
  onGoToPage,
  toolsets,
}: TakeoffViewerHeaderProps) {
  const exportActionLabel =
    exportStatus === GLOBAL_PROCESSING_COMPLETED
      ? "Re-export PDF"
      : exportStatus === GLOBAL_PROCESSING_FAILED
        ? "Retry export PDF"
        : exportStatus === GLOBAL_PROCESSING_PENDING
          ? "Queued..."
          : exportStatus === GLOBAL_PROCESSING_PROCESSING
            ? "Processing..."
            : "Export PDF";
  const exportStatusLabel =
    exportStatus === GLOBAL_PROCESSING_PENDING
      ? "Status: Queued"
      : exportStatus === GLOBAL_PROCESSING_PROCESSING
        ? "Status: Processing"
        : exportStatus === GLOBAL_PROCESSING_COMPLETED
          ? "Status: Ready"
          : exportStatus === GLOBAL_PROCESSING_FAILED
            ? "Status: Failed"
            : "Status: Not started";
  const exportStatusDetail =
    exportStatus === GLOBAL_PROCESSING_FAILED
      ? exportError ?? "Export failed. Retry to generate a fresh file."
      : exportStatus === GLOBAL_PROCESSING_COMPLETED
        ? "Latest export is ready to download."
        : exportStatus === GLOBAL_PROCESSING_PENDING ||
            exportStatus === GLOBAL_PROCESSING_PROCESSING
          ? "A new export is currently running."
          : "Queue an export to generate a PDF artifact.";

  return (
    <div className="pointer-events-none absolute inset-x-0 top-0 z-[60] flex">
      <div className="pointer-events-auto flex h-14 w-full items-center gap-3 border-b border-border bg-background/95 px-4 backdrop-blur">
        {/* Close Button */}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onClose}
          aria-label="Close viewer"
          className="shrink-0"
        >
          <X className="size-5" />
        </Button>

        <div className="mx-1 h-6 w-px bg-border" />

        {/* Document Picker */}
        <div className="w-56 shrink-0">
          <Select
            value={selectedPlanDocumentId}
            onValueChange={onSelectPlanDocumentId}
          >
            <SelectTrigger className="h-9 text-sm">
              <SelectValue placeholder="Select document" />
            </SelectTrigger>
            <SelectContent className="z-[160]">
              {viewerSources.map((source) => (
                <SelectItem key={source.sourceId} value={source.sourceId}>
                  {source.displayName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Page Selector */}
        {pageCount > 1 && onGoToPage && (
          <>
            <div className="mx-1 h-6 w-px bg-border" />
            <div className="flex items-center gap-1 shrink-0">
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                onClick={() => onGoToPage(activePage - 1)}
                disabled={activePage <= 1}
                aria-label="Previous page"
              >
                <ChevronLeft className="size-4" />
              </Button>
              <Select
                value={String(activePage)}
                onValueChange={(value) => onGoToPage(Number(value))}
              >
                <SelectTrigger
                  className="h-8 w-24 text-xs font-medium tabular-nums"
                  aria-label="Go to page"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-[160]">
                  {Array.from({ length: pageCount }, (_, i) => i + 1).map((page) => (
                    <SelectItem key={page} value={String(page)} className="text-xs">
                      Pg {page} / {pageCount}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                onClick={() => onGoToPage(activePage + 1)}
                disabled={activePage >= pageCount}
                aria-label="Next page"
              >
                <ChevronRight className="size-4" />
              </Button>
            </div>
          </>
        )}

        {/* Tags Count Badge */}
        {tagsCount > 0 && (
          <div className="hidden sm:flex items-center gap-1.5 rounded-md border border-border/60 bg-muted/30 px-3 py-1.5 text-xs font-medium text-muted-foreground shrink-0">
            <span>{tagsCount} tag{tagsCount === 1 ? "" : "s"}</span>
          </div>
        )}

        {/* Spacer */}
        <div className="flex-1" />

        {toolsets ? <div className="flex items-center gap-1">{toolsets}</div> : null}

        {/* Export/Download Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1.5"
              disabled={!canExportPdf && !canDownloadPdf && !canDownload}
              aria-label="Export options"
            >
              <Download className="size-4" />
              <span className="hidden sm:inline">Export</span>
              <ChevronDown className="size-3 opacity-50" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="z-[160] w-72">
            <div className="px-2 py-1.5">
              <p className="text-xs font-medium text-foreground">{exportStatusLabel}</p>
              <p className="mt-0.5 text-[11px] leading-snug text-muted-foreground">{exportStatusDetail}</p>
            </div>
            <DropdownMenuItem
              onClick={onExportPdf}
              disabled={!canExportPdf || isPdfExportInProgress}
              className="gap-2"
            >
              {isPdfExportInProgress ? (
                <Loader2 className="size-4 animate-spin" />
              ) : exportStatus === GLOBAL_PROCESSING_COMPLETED ? (
                <CheckCircle2 className="size-4" />
              ) : exportStatus === GLOBAL_PROCESSING_FAILED ? (
                <AlertCircle className="size-4" />
              ) : (
                <FileOutput className="size-4" />
              )}
              <span>{exportActionLabel}</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={onDownloadPdf}
              disabled={!canDownloadPdf}
              className="gap-2"
            >
              <Download className="size-4" />
              <span>Download PDF</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={onDownloadWorkbook}
              disabled={!canDownload}
              className="gap-2"
            >
              <Download className="size-4" />
              <span>Download CSV</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
